import { ObservableMap, makeAutoObservable, transaction } from 'mobx';

import { UserType } from '../constants';
import { FileDto, GroupDto, MessageDto } from '../dto';
import { GroupModel, MessageModel } from '../model';

class ChatStore {
  _searchKeyword = '';
  _groupMap: ObservableMap<number, GroupModel>;
  _messageMap: ObservableMap<number, MessageModel>;
  _currentGroup: GroupModel;
  _selectedFiles: File[];
  _placeholder: string;
  _myMessageChecker: boolean;
  _newChatStarted: boolean;
  _chatMessage = '';
  _tempMessageId = -1;
  _failCount = 0;

  constructor() {
    // this._currentGroup = new GroupModel();
    this._groupMap = new ObservableMap();
    this._messageMap = new ObservableMap();
    this._newChatStarted = false;
    this.init();
    makeAutoObservable(this);
  }
  init() {
    this._selectedFiles = [];
    this._messageMap.clear();
    this._newChatStarted = false;
    this._myMessageChecker = false;
    this._placeholder = '';
    this._currentGroup = new GroupModel();
  }

  initMessageStatus() {
    this._chatMessage = '';
    this._selectedFiles = [];
  }

  getMessage(id: number) {
    return this._messageMap.get(id);
  }

  appendTempMessage = () => {
    const tempMessageDto = {
      id: this.tempMessageId,
      groupId: this.getGroupId() as number,
      title: '',
      from: {
        id: -1,
        name: '',
        type: UserType.MANAGER,
        thumbnail: '',
      },
      content: this.chatMessage,
      files: this.selectedFiles as unknown as FileDto[],
      at: new Date().toISOString(),
      modified: false,
    };
    this.myMessageChecker = true;
    this.setMessageMapFromData([tempMessageDto]);
    return this._tempMessageId;
  };

  startNewChat = () => {
    this.init();
    this._newChatStarted = true;
    this.currentGroup = new GroupModel();
  };

  setGroupMapFromData(groupList: GroupDto[]) {
    transaction(() => {
      groupList.map((group) => {
        const targetGroup = new GroupModel(group);
        this._groupMap.set(group.id, targetGroup);
        if (this.currentGroup.id === group.id) {
          this.currentGroup = targetGroup;
        }
      });
    });
  }

  setMessageMapFromData(messageList: MessageDto[]) {
    transaction(() => {
      messageList.map((message) => {
        if (
          message.groupId === null ||
          message.groupId === this.currentGroup.id
        ) {
          this._messageMap.set(message.id, new MessageModel(message));
        }
      });
    });
  }

  setDefaultSelectedGroup(groupId?: number) {
    if (groupId && this._groupMap.get(groupId))
      this._currentGroup = this._groupMap.get(groupId) as GroupModel;
    else if (
      this._currentGroup.id === -1 &&
      this.groupList.length > 0 &&
      !this._newChatStarted
    )
      this.currentGroup = this.groupList[0];
    else {
      this.currentGroup = new GroupModel();
    }
  }
  removeTempMessage(id) {
    this._messageMap.delete(id);
  }
  getGroupId() {
    if (this._currentGroup.id === -1) return null;
    return this.currentGroup.id;
  }

  get tempMessageId() {
    return --this._tempMessageId;
  }
  get groupList() {
    return (
      Array.from(this._groupMap.values()).sort(
        (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()
      ) || []
    );
  }
  get chatMessage() {
    return this._chatMessage;
  }
  set chatMessage(value: string) {
    this._chatMessage = value;
  }
  get messageList() {
    const existingMessages =
      Array.from(this._messageMap.values())
        .filter((message) => message.id > 0)
        .sort((a, b) => a.id - b.id) || [];
    const tempMessages =
      Array.from(this._messageMap.values())
        .filter((message) => message.id < 0)
        .sort((a, b) => b.id - a.id) || [];
    return [...existingMessages, ...tempMessages];
  }
  get searchKeyword() {
    return this._searchKeyword;
  }
  set searchKeyword(value: string) {
    this._searchKeyword = value;
  }
  get currentGroup() {
    return this._currentGroup;
  }
  set currentGroup(group: GroupModel) {
    this._currentGroup = group;
  }
  get lastMessageId() {
    const existingMessages = this.messageList.filter(
      (message) => message.id > 0
    );
    return existingMessages[existingMessages.length - 1]?.id;
  }
  get selectedFiles() {
    return this._selectedFiles || [];
  }
  set selectedFiles(value: File[]) {
    this._selectedFiles = value;
  }
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
  }
  get myMessageChecker() {
    return this._myMessageChecker;
  }
  set myMessageChecker(value: boolean) {
    this._myMessageChecker = value;
  }
  get newChatStarted() {
    return this._newChatStarted;
  }
  set newChatStarted(value: boolean) {
    this._newChatStarted = value;
  }
  get failCount() {
    return this._failCount;
  }

  addFailCount() {
    this._failCount += 1;
  }
  initFailCount() {
    this._failCount = 0;
  }
  emptyGroupMap() {
    transaction(() => {
      this._currentGroup = new GroupModel();
      this._groupMap.clear();
    });
  }
  emptyMessageMap() {
    this._messageMap.clear();
  }
}

const chatStoreImpl = new ChatStore();
export default chatStoreImpl;
